import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import apiService from '../../services/api';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import './ResourceDashboard.css';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend, Label, ResponsiveContainer
} from 'recharts';
import { Snackbar } from '@mui/material';
 
// Icons
const downloadIcon = apiService.getBlobAssetUrl('download.svg');
const sendIcon = apiService.getBlobAssetUrl('send_12.svg');
const closeimage = apiService.getBlobAssetUrl('close_12.svg');
 
const ResourceDashboard = ({ surveyId, token }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [showEmailMenu, setShowEmailMenu] = useState(false);
  const [email, setEmail] = useState('');
  const [users, setUsers] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const dashboardRef = useRef(null);
 
  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const response = await apiService.fetchData(`reports/resource-metrics/${surveyId}`);
        console.log('ScoreDistribution:', response.data.ScoreDistribution);
        if (response.status === 'success') {
          setData(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch Resource metrics:', error);
        setError('Failed to load resource metrics.');
        setSnackbarMessage('Failed to load resource metrics.');
        setSnackbarOpen(true);
      }
    };
 
    const loadUserInfo = async () => {
      try {
        const data = await apiService.fetchData('userslist');
        const results = data?.results || [];
        const formatted = results.map((item) => ({
          ID: item.ID || '',
          Name: item.Name || '',
          EmailID: item.EmailID || '',
        }));
        setUsers(formatted);
      } catch (error) {
        console.error('Error fetching users:', error);
        setSnackbarMessage('Failed to load users');
        setSnackbarOpen(true);
      }
    };
 
    fetchMetrics();
    loadUserInfo();
  }, [surveyId]);
 
  const handleDownload = () => {
    const csvHeader = 'Project,Avg CSAT,Promoters,Neutrals,Detractors,Response Count,Last Survey Date\n';
    const csvRows = data.ProjectWiseCSAT.map(proj =>
      `${proj.ProjectName},${proj.AvgCSATScore},${proj.Promoters},${proj.Neutrals},${proj.Detractors},${proj.ResponseCount},${formatDate(proj.LastSurveyDate)}`
    ).join('\n');
 
    const csvContent = csvHeader + csvRows;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'ProjectWiseResourceCSATScores.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
 
  const handleDownloadPDF = () => {
    const element = dashboardRef.current;
    if (!element) {
      console.error('Dashboard element not found in ref');
      setSnackbarMessage('Failed to capture dashboard for PDF');
      setSnackbarOpen(true);
      return;
    }
 
    const clonedElement = element.cloneNode(true);
    clonedElement.style.position = 'absolute';
    clonedElement.style.top = '0';
    clonedElement.style.left = '0';
    clonedElement.style.zIndex = '-1';
    clonedElement.style.background = '#fafafa';
    clonedElement.style.width = '100%';
    clonedElement.style.padding = '20px';
    clonedElement.style.overflow = 'visible';
 
    // Hide Download and Share buttons
    const downloadButton = clonedElement.querySelector('.resource-dashboard-actions-right');
    if (downloadButton) downloadButton.style.display = 'none';
 
    document.body.appendChild(clonedElement);
 
    html2canvas(clonedElement, {
      useCORS: true,
      scale: 1,
      scrollX: 0,
      scrollY: 0,
      windowWidth: clonedElement.scrollWidth,
      windowHeight: clonedElement.scrollHeight,
    })
      .then((canvas) => {
        const imgData = canvas.toDataURL('image/jpeg', 0.5);
        const pdfWidth = clonedElement.scrollWidth / 96 * 25.4;
        const pdfHeight = clonedElement.scrollHeight / 96 * 25.4;
 
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: [pdfWidth, pdfHeight],
          compress: true
        });
        pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
        pdf.save('Resource_Metrics_Dashboard.pdf');
 
        document.body.removeChild(clonedElement);
      })
      .catch((error) => {
        console.error('Error generating PDF:', error);
        setSnackbarMessage('Failed to export PDF');
        setSnackbarOpen(true);
        document.body.removeChild(clonedElement);
      });
  };
 
  const handleSendDashboardFile = async (fileType) => {
    if (!email) {
      setSnackbarMessage('Please enter an email address.');
      setSnackbarOpen(true);
      return;
    }
 
    const trimmedEmail = email.trim().toLowerCase();
    const element = dashboardRef.current;
 
    if (!element) {
      console.error('Dashboard element not found in ref');
      setSnackbarMessage('Failed to capture dashboard for email');
      setSnackbarOpen(true);
      return;
    }
 
    const emailButton = document.querySelector('.resource-dashboard-actions-right');
    const emailDropdown = document.querySelector('#dashboard-email-dropdown-1');
    const originalEmailButtonStyle = emailButton ? emailButton.style.display : '';
    const originalEmailDropdownStyle = emailDropdown ? emailDropdown.style.display : '';
 
    if (emailButton) emailButton.style.display = 'none';
    if (emailDropdown) emailDropdown.style.display = 'none';
 
    const clonedElement = element.cloneNode(true);
    clonedElement.style.position = 'absolute';
    clonedElement.style.top = '0';
    clonedElement.style.left = '0';
    clonedElement.style.zIndex = '-1';
    clonedElement.style.background = '#fafafa';
    clonedElement.style.width = '100%';
    clonedElement.style.padding = '20px';
    clonedElement.style.overflow = 'visible';
 
    // Hide Download and Share buttons
    const downloadButton = clonedElement.querySelector('.resource-dashboard-actions-right');
    if (downloadButton) downloadButton.style.display = 'none';
 
    document.body.appendChild(clonedElement);
 
    try {
      let attachment;
      if (fileType === 'pdf') {
        const canvas = await html2canvas(clonedElement, {
          useCORS: true,
          scale: 1,
          scrollX: 0,
          scrollY: 0,
          windowWidth: clonedElement.scrollWidth,
          windowHeight: clonedElement.scrollHeight,
        });
        if (!canvas) {
          throw new Error('Canvas generation failed');
        }
 
        const imgData = canvas.toDataURL('image/jpeg', 0.5);
        const pdfWidth = clonedElement.scrollWidth / 96 * 25.4;
        const pdfHeight = clonedElement.scrollHeight / 96 * 25.4;
 
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: [pdfWidth, pdfHeight],
          compress: true
        });
        pdf.addImage(imgData, 'JPEG', 0.1, 0.1, pdfWidth - 0.2, pdfHeight - 0.2);
        attachment = pdf.output('datauristring');
      }
 
      const emailData = {
        recipientName: trimmedEmail.split('@')[0] || 'Recipient',
        organizationName: 'CriticalRiver Technologies',
        dashboardTitle: 'Resource Metrics Dashboard',
        to: trimmedEmail,
        subject: `Resource Metrics Dashboard (${fileType.toUpperCase()})`,
        attachment: attachment,
      };
 
      const response = await apiService.sendDashboardEmail(
        emailData.recipientName,
        null,
        null,
        emailData.organizationName,
        emailData.dashboardTitle,
        emailData.to,
        emailData.subject,
        token,
        emailData.attachment
      );
 
      setSnackbarMessage(response.message || `Report sent successfully to ${trimmedEmail}!`);
      setSnackbarOpen(true);
      setEmail('');
      setShowEmailMenu(false);
    } catch (error) {
      console.error('Error sending dashboard email:', error);
      console.error('Full error details:', {
        message: error.message,
        response: error.response ? error.response.data : 'No response',
        status: error.response ? error.response.status : 'Unknown',
      });
      if (error.message.includes('554') || error.message.includes('MaxSubmissionExceeded')) {
        setSnackbarMessage('Email failed: Attachment size exceeds server limit. Please download the file manually.');
      } else {
        setSnackbarMessage(error.response?.data?.error || error.message || 'Failed to send dashboard email');
      }
      setSnackbarOpen(true);
    } finally {
      if (emailButton) emailButton.style.display = originalEmailButtonStyle;
      if (emailDropdown) emailDropdown.style.display = originalEmailDropdownStyle;
 
      if (clonedElement.parentNode === document.body) {
        document.body.removeChild(clonedElement);
      }
    }
  };
 
  const handleClearInput = () => {
    setEmail('');
    setSuggestions([]);
  };
 
  const handleEmailInputChange = (e) => {
    const input = e.target.value;
    setEmail(input);
 
    if (input.trim() === '') {
      setSuggestions([]);
      return;
    }
 
    const filteredSuggestions = users.filter(
      (user) =>
        user?.EmailID?.toLowerCase().includes(input.toLowerCase()) ||
        user?.Name?.toLowerCase().includes(input.toLowerCase())
    );
 
    setSuggestions(filteredSuggestions);
  };
 
  const handleSuggestionClick = (user) => {
    setEmail(user.EmailID);
    setSuggestions([]);
  };
 
  const renderSuggestionText = (text, searchTerm) => {
    if (!searchTerm) return <span>{text}</span>;
 
    const lowerText = text?.toLowerCase() ?? '';
    const lowerSearchTerm = searchTerm.toLowerCase();
    const index = lowerText.indexOf(lowerSearchTerm);
 
    if (index === -1) return <span>{text}</span>;
 
    const beforeMatch = text.substring(0, index);
    const match = text.substring(index, index + searchTerm.length);
    const afterMatch = text.substring(index + searchTerm.length);
 
    return (
      <>
        <span className="suggestion-non-match">{beforeMatch}</span>
        <span className="suggestion-match">{match}</span>
        <span className="suggestion-non-match">{afterMatch}</span>
      </>
    );
  };
 
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };
 
  if (!data) return <div>Loading Resource dashboard...</div>;
 
  const COLORS = ['#FF7300', '#001f4d', '#2980b9', '#2ecc71', '#9b59b6', '#f39c12'];
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
 
  return (
    <>
      <div className="resource-dashboard-actions-right" style={{ position: 'relative', marginBottom: '15px' }}>
        <button
          className="resource-icon-btn"
          id="settings-download-wrapper2"
          onClick={handleDownloadPDF}
          aria-label="Download entire dashboard as PDF"
        >
          <img src={downloadIcon} alt="Download" className="resource-action-icon" />
          <span>Download</span>
        </button>
        <button
          className="resource-icon-btn"
          id="settings-email-wrapper"
          onClick={() => setShowEmailMenu(true)}
          aria-label="Toggle email options for entire dashboard"
        >
          <img src={sendIcon} alt="Send" className="resource-action-icon" />
          <span>Share</span>
        </button>
        {showEmailMenu && (
          <div className="dashboard-email-dropdown" id="dashboard-email-dropdown-1">
            <div className="email-input-wrapper">
              <input
                type="email"
                value={email}
                aria-label="Enter email address"
                onChange={handleEmailInputChange}
                placeholder="Enter email address"
                style={{ padding: '5px', marginBottom: '5px', width: '100%', minWidth: '180px' }}
              />
              {email && (
                <button
                  type="button"
                  className="clear-img11"
                  id="clear-img11-1"
                  onClick={handleClearInput}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleClearInput();
                    }
                  }}
                  aria-label="Clear email input"
                  tabIndex={0}
                >
                  <img src={closeimage} alt="clear" />
                </button>
              )}
              {suggestions.length > 0 && (
                <ul className="suggestions-dropdown" id="suggestions-dropdown-1">
                  {suggestions.map((user, index) => (
                    <li
                      key={user.ID}
                      onClick={() => handleSuggestionClick(user)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleSuggestionClick(user);
                        }
                      }}
                      className={`suggestion-item ${index % 2 === 0 ? 'even' : 'odd'}`}
                      tabIndex={0}
                    >
                      <div>{renderSuggestionText(user.Name, email)}</div>
                      <div>{renderSuggestionText(user.EmailID, email)}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div
              className="dashboard-export-option"
              onClick={() => handleSendDashboardFile('pdf')}
            >
              Send PDF
            </div>
          </div>
        )}
      </div>
      {error && <p style={{ color: 'red' }}>{error}</p>}
      <div className="ams-dashboard-wrapper" ref={dashboardRef}>
        <div className="ams-dashboard">
          <div className="dashboard-section">
            <div className="cards-container">
              {[
                { label: 'Overall CSAT', value: data.OverallCSAT[0]?.OverallCSATAverage, className: 'orange' },
                { label: 'Promoters', value: `${data.Promoters[0]?.Promoters}%`, className: 'green' },
                { label: 'Neutral', value: `${data.Neutral[0]?.Neutral}%`, className: 'blue' },
                { label: 'Detractors', value: `${data.Detractors[0]?.Detractors}%`, className: 'darkblue' },
                { label: 'Response Count', value: data.ResponseCount[0]?.ResponseCount, className: 'purple' },
              ].map((card) => (
                <div key={card.label} className={`resource-card ${card.className}`}>
                  <span className="resource-card-title">{card.label}</span>
                  <span className="resource-card-value">{card.value || 'N/A'}</span>
                </div>
              ))}
            </div>
          </div>
 
          <div className="resource-dashboard-section">
            <div className="resource-charts-container resource-no-top-space">
              <div className="resource-chart-box bar-chart">
                <h4>Section Score Distribution</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.SectionScoreDistribution}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="SectionName" tick={false}>
                      <Label
                        value="Sections"
                        position="insideBottom"
                        dy={0}
                        style={{ fontWeight: 'bold', fontSize: 14, fill: '#000' }}
                      />
                    </XAxis>
                    <YAxis domain={[0, 5]} ticks={[0, 1, 2, 3, 4, 5]}>
                      <Label
                        value="Avg CSAT"
                        angle={-90}
                        position="insideLeft"
                        dy={0}
                        style={{ fontWeight: 'bold', fontSize: 14, fill: '#000', textAnchor: 'middle' }}
                      />
                    </YAxis>
                    <Tooltip
                      formatter={(value, name, props) => [
                        value,
                        name === 'AverageCSATScore' ? 'Average CSAT' : 'Std Deviation',
                        `Responses: ${props.payload.ResponseCount}`,
                      ]}
                    />
                    <Bar dataKey="AverageCSATScore" fill="#001f4d" barSize={20} />
                    <Bar dataKey="ScoreStandardDeviation" fill="#ff7300" barSize={20} />
                    <Legend />
                  </BarChart>
                </ResponsiveContainer>
              </div>
 
              <div className="resource-chart-box pie-chart">
                <h4>Score Distribution</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={data.ScoreDistribution}
                      dataKey="ResponsePercentage"
                      nameKey="ScoreRange"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      label={({ name, percent }) => `${(percent * 100).toFixed(1)}%`}
                    >
                      {data.ScoreDistribution.map((entry, index) => (
                        <Cell key={`cell-${entry.ScoreRange}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Legend />
                    <Tooltip
                      formatter={(value, name) => [`${value}%`, name]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
 
              <div className="resource-chart-box bar-chart">
                <h4>CSAT Score Trend</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.ScoreTrend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="MonthNumber"
                      tickFormatter={(monthNum) => monthNames[monthNum - 1]}
                      interval={0}
                      margin={{ top: 20, right: 20, bottom: 70, left: 30 }}
                    >
                      <Label value="Months" position="insideBottom" dy={10} style={{ fontWeight: 'bold', fontSize: 14, fill: '#000' }} />
                    </XAxis>
                    <YAxis domain={[0, 5]} ticks={[0, 1, 2, 3, 4, 5]}>
                      <Label
                        value="CSAT-Scores"
                        angle={-90}
                        position="insideLeft"
                        dy={-30}
                        style={{ fontWeight: 'bold', fontSize: 14, textAnchor: 'middle', fill: '#000' }}
                      />
                    </YAxis>
                    <Tooltip />
                    <Bar dataKey="AverageCSAT" fill="#2980b9" barSize={40} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
 
          <div className="resource-dashboard-section">
            <div className="resource-table-header">
              <h4>Project-wise CSAT Scores</h4>
              <button className="resource-download-btn" onClick={handleDownload}>
                <img src={downloadIcon} alt="Download" className="resource-download-icon" /> Download
              </button>
            </div>
            <table className="resource-csat-table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Avg CSAT</th>
                  <th>Promoters</th>
                  <th>Neutrals</th>
                  <th>Detractors</th>
                  <th>Response Count</th>
                  <th>Last Survey Date</th>
                </tr>
              </thead>
              <tbody>
                {data.ProjectWiseCSAT.map((proj) => (
                  <tr key={proj.ProjectName}>
                    <td>{proj.ProjectName}</td>
                    <td>{proj.AvgCSATScore}</td>
                    <td>{proj.Promoters} %</td>
                    <td>{proj.Neutrals} %</td>
                    <td>{proj.Detractors} %</td>
                    <td>{proj.ResponseCount}</td>
                    <td>{formatDate(proj.LastSurveyDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        sx={{ '& .MuiSnackbarContent-root': { backgroundColor: snackbarMessage.includes('successful') ? '#4caf50' : '#f44336' } }}
      />
    </>
  );
};
 
ResourceDashboard.propTypes = {
  surveyId: PropTypes.number.isRequired,
  token: PropTypes.string.isRequired,
};
 
export default ResourceDashboard;
 